import { useState } from "react";
import { CheckCircle2, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { submitLead, BROCHURE_URL, apiError } from "../../lib/api";
import { INDUSTRIES_WELCOME, PRICE_TABLE } from "../../config/site";

const FIELD_SETS = {
  site_visit: ["name", "phone", "company", "industry", "plot_size", "preferred_date"],
  brochure: ["name", "phone", "company", "industry"],
  plot_enquiry: ["name", "phone", "company", "message"],
  channel_partner: ["name", "phone", "company", "message"],
};

const TITLES = {
  site_visit: { title: "Book a Site Visit", sub: "Walk the land, see the approvals, meet our team. We will call you to confirm." },
  brochure: { title: "Download the BIDC Brochure", sub: "Share your details and the brochure is yours — instant download." },
  plot_enquiry: { title: "Enquire about this plot", sub: "Tell us how to reach you and our team will call with availability & best price." },
  channel_partner: { title: "Partner with BIDC", sub: "Channel partners and brokers — let's talk inventory, commissions and support." },
};

const PLOT_SIZE_OPTIONS = [...PRICE_TABLE.map((p) => p.label), "Plug & play shed (lease)", "Not sure yet"];
const INDUSTRY_OPTIONS = [...INDUSTRIES_WELCOME, "Other"];

const inputCls =
  "bg-paper-light border-border text-ink placeholder:text-[#4A4C4A]/50 focus-visible:ring-gold h-11";

export const LeadDialog = ({ type, defaults = {}, children }) => {
  const meta = TITLES[type];
  const fields = FIELD_SETS[type];
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitLead({ type, ...defaults, ...form });
      setDone(true);
      toast.success("Thank you! Our team will reach out shortly.");
    } catch (err) {
      toast.error(apiError(err, "Could not submit. Please try WhatsApp instead."));
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setForm({});
    setDone(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) setTimeout(reset, 250);
      }}
    >
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="bg-paper-light border-border text-ink sm:max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-forest">{meta.title}</DialogTitle>
          <DialogDescription className="text-[#4A4C4A]">{meta.sub}</DialogDescription>
        </DialogHeader>

        {done ? (
          <div className="py-6 text-center" data-testid={`${type}-success`}>
            <CheckCircle2 className="mx-auto h-12 w-12 text-forest-light" strokeWidth={1.5} />
            <p className="mt-4 font-display text-xl text-forest">Dhanyavaad! Details received.</p>
            <p className="mt-1 text-sm text-[#4A4C4A]">
              Our team will contact you within working hours.
            </p>
            {type === "brochure" && (
              <Button
                asChild
                className="mt-6 bg-gold text-forest-dark hover:bg-gold-dark hover:text-paper"
                data-testid="brochure-download-button"
              >
                <a href={BROCHURE_URL} download="BIDC-Brochure.pdf">
                  <Download className="mr-2 h-4 w-4" /> Download Brochure (PDF)
                </a>
              </Button>
            )}
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4" data-testid={`${type}-form`}>
            {(defaults.plot_id || defaults.plot_size) && (
              <div className="border border-gold/50 bg-gold-pale/60 px-4 py-3 text-sm text-forest">
                Enquiring for:{" "}
                <strong>
                  {defaults.plot_id && `${defaults.plot_id} · `}
                  {defaults.plot_size}
                </strong>
              </div>
            )}

            {fields.includes("name") && (
              <div className="space-y-1.5">
                <Label htmlFor={`${type}-name`}>Full name *</Label>
                <Input
                  id={`${type}-name`}
                  data-testid={`${type}-name-input`}
                  required
                  minLength={2}
                  placeholder="Your name"
                  className={inputCls}
                  value={form.name || ""}
                  onChange={(e) => set("name", e.target.value)}
                />
              </div>
            )}

            {fields.includes("phone") && (
              <div className="space-y-1.5">
                <Label htmlFor={`${type}-phone`}>Phone *</Label>
                <Input
                  id={`${type}-phone`}
                  data-testid={`${type}-phone-input`}
                  required
                  type="tel"
                  inputMode="tel"
                  placeholder="10-digit mobile number"
                  className={inputCls}
                  value={form.phone || ""}
                  onChange={(e) => set("phone", e.target.value)}
                />
              </div>
            )}

            {fields.includes("company") && (
              <div className="space-y-1.5">
                <Label htmlFor={`${type}-company`}>
                  {type === "channel_partner" ? "Firm / Agency" : "Company"}
                </Label>
                <Input
                  id={`${type}-company`}
                  data-testid={`${type}-company-input`}
                  placeholder={type === "channel_partner" ? "Your firm name" : "Company name"}
                  className={inputCls}
                  value={form.company || ""}
                  onChange={(e) => set("company", e.target.value)}
                />
              </div>
            )}

            {fields.includes("industry") && (
              <div className="space-y-1.5">
                <Label>Industry</Label>
                <Select value={form.industry || ""} onValueChange={(v) => set("industry", v)}>
                  <SelectTrigger data-testid={`${type}-industry-select`} className={inputCls}>
                    <SelectValue placeholder="Select your industry" />
                  </SelectTrigger>
                  <SelectContent className="bg-paper-light">
                    {INDUSTRY_OPTIONS.map((o) => (
                      <SelectItem key={o} value={o}>
                        {o}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {fields.includes("plot_size") && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Plot size of interest</Label>
                  <Select value={form.plot_size || ""} onValueChange={(v) => set("plot_size", v)}>
                    <SelectTrigger data-testid={`${type}-plot-size-select`} className={inputCls}>
                      <SelectValue placeholder="Select size" />
                    </SelectTrigger>
                    <SelectContent className="bg-paper-light">
                      {PLOT_SIZE_OPTIONS.map((o) => (
                        <SelectItem key={o} value={o}>
                          {o}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor={`${type}-date`}>Preferred visit date</Label>
                  <Input
                    id={`${type}-date`}
                    data-testid={`${type}-date-input`}
                    type="date"
                    className={inputCls}
                    value={form.preferred_date || ""}
                    onChange={(e) => set("preferred_date", e.target.value)}
                  />
                </div>
              </div>
            )}

            {fields.includes("message") && (
              <div className="space-y-1.5">
                <Label htmlFor={`${type}-message`}>Message</Label>
                <Textarea
                  id={`${type}-message`}
                  data-testid={`${type}-message-input`}
                  rows={3}
                  placeholder={
                    type === "channel_partner"
                      ? "Areas you cover, typical client profile…"
                      : "Anything we should know?"
                  }
                  className="bg-paper-light border-border text-ink placeholder:text-[#4A4C4A]/50 focus-visible:ring-gold"
                  value={form.message || ""}
                  onChange={(e) => set("message", e.target.value)}
                />
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              data-testid={`${type}-submit-button`}
              className="w-full h-11 bg-forest text-paper hover:bg-forest-light transition-colors duration-200"
            >
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {type === "brochure" ? "Get the Brochure" : type === "site_visit" ? "Request Visit" : "Submit Enquiry"}
            </Button>
            <p className="text-[11px] text-center text-[#4A4C4A]/70">
              We respect your privacy. No spam — only a call from our Bhiwani team.
            </p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
